import { ConfigProvider } from "antd";
import type { Locale } from "antd/es/locale";
import enUS from "antd/locale/en_US";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { loadAntdLocale } from "./antdLocale";
import { loadDayjsLocale } from "./dayjsLocale";
import { SUPPORTED_LOCALES } from "./detectLanguage";
import type { SupportedLocale } from "./detectLanguage";

interface LocaleProviderProps {
  children: ReactNode;
}

const isSupported = (value: string): value is SupportedLocale =>
  (SUPPORTED_LOCALES as readonly string[]).includes(value);

export const LocaleProvider = ({ children }: LocaleProviderProps) => {
  const { i18n } = useTranslation();
  const [antdLocale, setAntdLocale] = useState<Locale>(enUS);

  useEffect(() => {
    let cancelled = false;

    const apply = async (lang: string) => {
      const normalized: SupportedLocale = isSupported(lang) ? lang : "en";
      const [loaded] = await Promise.all([
        loadAntdLocale(normalized),
        loadDayjsLocale(normalized),
      ]);
      if (cancelled) return;
      setAntdLocale(loaded);
      document.documentElement.lang = normalized;
    };

    void apply(i18n.language);

    const handler = (lng: string) => {
      void apply(lng);
    };
    i18n.on("languageChanged", handler);

    return () => {
      cancelled = true;
      i18n.off("languageChanged", handler);
    };
  }, [i18n]);

  return <ConfigProvider locale={antdLocale}>{children}</ConfigProvider>;
};

export default LocaleProvider;
